import type { MouseEvent } from 'react';
import React, { useCallback, useEffect, useState } from 'react';
import DOMPurify from 'dompurify';

import { DangerIcon, InfoFilledIcon, WarningBasicIcon } from '@genially/icons';

import { containsHTML } from '../../../utils/containsHTML';
import { sleep } from '../../../utils/sleep';
import { Button } from '../../buttons/Button/Button';
import { Variant } from './types';
import {
  ButtonWrapper,
  Container,
  ContentWrapper,
  IconTextWrapper,
  IconWrapper,
  StyledBasicParagraph,
  StyledButtonText,
  StyledCloseIcon,
  StyledVariantIcon,
  Wrapper,
} from './Banner.styled';

interface BannerProps {
  /**
   * Text to display on the banner
   */
  text: string;
  /**
   * Variant on which the style of the button depends. Options are:
   * `Banner.Variant.INFO`, `Banner.Variant.WARNING`, `Banner.Variant.DANGER`
   */
  variant: Variant;
  /**
   * Indicates whether the banner is displayed
   */
  show?: boolean;
  /**
   * Text to display on the button
   */
  buttonText?: string;
  /**
   * Property used for testing purposes
   */
  dataCy?: string;
  /**
   * DataCy for the button
   */
  buttonDataCy?: string;
  /**
   * Identifier for Google Tag Manager
   */
  dataTm?: string;
  /**
   * Indicates the time in ms that the banner takes to disappear
   */
  hideAfter?: number;
  /**
   * Function for button action. It is executed when the button is clicked
   */
  buttonAction?: (e: MouseEvent<Element>) => void;
  /**
   * Function that is executed when the banner is closed
   */
  onClose?: () => void;
}

const getVariantIcon = (variant: Variant) => {
  switch (variant) {
    case Variant.WARNING:
      return WarningBasicIcon;
    case Variant.DANGER:
      return DangerIcon;
    case Variant.INFO:
    default:
      return InfoFilledIcon;
  }
};

const Banner = ({
  text,
  show = false,
  variant,
  buttonText,
  hideAfter,
  buttonAction,
  onClose,
  dataCy,
  buttonDataCy,
  dataTm,
}: BannerProps) => {
  const [isVisible, setIsVisible] = useState(show);

  const handleOnClose = useCallback(() => {
    onClose?.();
    setIsVisible(false);
  }, [onClose]);

  const hideTemporalBanner = useCallback(() => {
    if (hideAfter) {
      sleep(hideAfter).then(() => {
        handleOnClose();
      });
    }
  }, [hideAfter, handleOnClose]);

  useEffect(() => {
    setIsVisible(show);
  }, [show]);

  useEffect(() => {
    if (show) {
      hideTemporalBanner();
    }
  }, [show, hideTemporalBanner]);

  const handleClickButton = (e: MouseEvent) => buttonAction?.(e);

  return (
    <Wrapper $show={isVisible} data-cy={dataCy}>
      <Container $show={isVisible} $variant={variant}>
        <ContentWrapper $show={isVisible}>
          <IconTextWrapper>
            <StyledVariantIcon
              as={getVariantIcon(variant)}
              $show={isVisible}
              $variant={variant}
            />
            {containsHTML(text) ? (
              <StyledBasicParagraph
                $variant={variant}
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(text) }}
              />
            ) : (
              <StyledBasicParagraph $variant={variant}>{text}</StyledBasicParagraph>
            )}
          </IconTextWrapper>
          {!!buttonText && (
            <ButtonWrapper data-tm={dataTm}>
              <Button
                dataCy={buttonDataCy}
                onClick={handleClickButton}
                size={Button.Size.SMALL}
                variant={Button.Variant.PLAIN}
              >
                <StyledButtonText $variant={variant}>{buttonText}</StyledButtonText>
              </Button>
            </ButtonWrapper>
          )}
        </ContentWrapper>
        <IconWrapper $show={isVisible}>
          <StyledCloseIcon onClick={handleOnClose} />
        </IconWrapper>
      </Container>
    </Wrapper>
  );
};

Banner.Variant = Variant;

export default Banner;
